import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { LineSeries } from '@nivo/line';
import { area, curveMonotoneX } from 'd3-shape';
import { SimpleProjectionYearResult } from '../../../../../types/SimpleProjection';
import { useStockCurrency } from '../../../../../contexts/StockCurrencyContext';
import { getChartColors, CHART_COLORS } from '../../../Charts/constants/chartColors';
import ChartTooltip from '../../../Charts/components/shared/ChartTooltip';
import GenericFinancialLineChart from '../../../Charts/components/shared/GenericFinancialLineChart';

interface ProjectedSharePriceChartProps {
  projections: SimpleProjectionYearResult[];
  ticker: string;
  transparentBg?: boolean;
}

const formatSharePrice = (value: number, currencySymbol: string): string => {
  const abs = Math.abs(value);
  const formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${value >= 0 ? currencySymbol : `-${currencySymbol}`}${formatted}`;
};

const HIGH_SERIES_ID = 'Share Price High';
const LOW_SERIES_ID = 'Share Price Low';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GlowLayer = ({ series, innerHeight, innerWidth }: any) => {
  const instanceId = React.useId().replace(/:/g, '');

  if (!series || series.length === 0) return null;

  // Sort by average Y position — lowest Y = top of chart = highest price
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortedSeries = [...series].sort((a: any, b: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const avgA = a.data.reduce((sum: number, d: any) => sum + (d.position.y ?? 0), 0) / a.data.length;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const avgB = b.data.reduce((sum: number, d: any) => sum + (d.position.y ?? 0), 0) / b.data.length;
    return avgA - avgB;
  });

  const clipId = `share-price-glow-clip-${instanceId}`;
  const gradientId = (serieId: string | number) =>
    `share-price-glow-${instanceId}-${String(serieId).replace(/\s+/g, '-')}`;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={innerWidth} height={innerHeight} />
        </clipPath>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {sortedSeries.map((serie: any) => (
          <linearGradient
            key={String(serie.id)}
            id={gradientId(serie.id)}
            x1="0"
            x2="0"
            y1="0"
            y2="1"
          >
            <stop offset="0%" stopColor={serie.color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={serie.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {sortedSeries.map((serie: any, serieIndex: number) => {
          const nextSerie = sortedSeries[serieIndex + 1];

          const points = serie.data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((d: any) => d.position.x != null && d.position.y != null)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((d: any) => ({ x: d.position.x as number, y: d.position.y as number }));

          if (points.length < 2) return null;

          const baselineYMap = new Map<number, number>();
          if (nextSerie) {
            nextSerie.data
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .filter((d: any) => d.position.x != null && d.position.y != null)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .forEach((d: any) => baselineYMap.set(d.position.x as number, d.position.y as number));
          }

          const areaGen = area<{ x: number; y: number }>()
            .x((d) => d.x)
            .y1((d) => d.y)
            .y0((d) => baselineYMap.get(d.x) ?? innerHeight)
            .curve(curveMonotoneX);

          const path = areaGen(points);
          if (!path) return null;

          return (
            <path
              key={String(serie.id)}
              d={path}
              fill={`url(#${gradientId(serie.id)})`}
            />
          );
        })}
      </g>
    </g>
  );
};

const ProjectedSharePriceChart: React.FC<ProjectedSharePriceChartProps> = ({ projections, ticker, transparentBg = false }) => {
  const theme = useTheme();
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(theme.palette.mode === 'dark');
  const isDark = theme.palette.mode === 'dark';

  const { chartData, hasNegativeValues } = useMemo(() => {
    // Drop null points (years where EPS was negative — share price isn't
    // editorially meaningful) so the line renders as a gap rather than
    // collapsing onto the x-axis.
    const allPrices = projections
      .flatMap((p) => [p.sharePriceLow, p.sharePriceHigh])
      .filter((p): p is number => p !== null);
    const hasNegatives = allPrices.some((p) => p < 0);

    const data: LineSeries[] = [
      {
        id: HIGH_SERIES_ID,
        data: projections
          .filter((p) => p.sharePriceHigh !== null)
          .map((p) => ({
            x: p.isHistorical ? `${p.yearLabel} (A)` : p.yearLabel,
            y: p.sharePriceHigh as number,
          })),
      },
      {
        id: LOW_SERIES_ID,
        data: projections
          .filter((p) => p.sharePriceLow !== null)
          .map((p) => ({
            x: p.isHistorical ? `${p.yearLabel} (A)` : p.yearLabel,
            y: p.sharePriceLow as number,
          })),
      },
    ];

    return { chartData: data, hasNegativeValues: hasNegatives };
  }, [projections]);

  const getSeriesColor = (serie: LineSeries): string => {
    if (hasNegativeValues) return colors.negative;
    return serie.id === HIGH_SERIES_ID
      ? CHART_COLORS.DATA.POSITIVE_DARK
      : isDark ? '#34d399' : CHART_COLORS.DATA.POSITIVE_LIGHT;
  };

  return (
    <GenericFinancialLineChart
      title="Projected Share Price Range"
      ticker={ticker}
      height={350}
      renderCard={!transparentBg}
      isLoading={false}
      error={null}
      chartData={chartData}
      isEmpty={projections.length === 0}
      colors={getSeriesColor}
      yAxisLegend="Share Price"
      yAxisFormat={(value) => formatSharePrice(value, currencySymbol)}
      enableCrosshair={false}
      customLayers={[GlowLayer]}
      tooltip={(point) => (
        <ChartTooltip
          header={String(point.data.x)}
          headerColor={point.serieColor}
          rows={[{
            label: point.seriesId === HIGH_SERIES_ID ? 'High' : 'Low',
            value: formatSharePrice(point.data.y as number, currencySymbol),
            color: point.serieColor,
          }]}
        />
      )}
    />
  );
};

export default ProjectedSharePriceChart;
